import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { AICoachGoalPriority, AICoachGoalType } from 'enums/AICoach';
import { AICoachGoalItem } from 'api/LoanOfferApi';
import { setActiveWorkflow, WorkflowTypes, toggleAIChat } from 'handlers/aiChat';
import {
  updateAICoachGoals,
  getFinancialMetrics,
  getCreditScoreHistory,
  getGoalProgress,
  getSavingsAccounts,
  paraphraseGoal,
  paraphraseNextStep,
  getNextSteps,
  updateApplicationCurrentFlow,
} from 'thunks';
import {
  addMessage,
  removeDecisionAction,
  Action,
  updateLastMessage,
  ActionOverrideType,
} from 'handlers/goalOptimizer';
import {
  getAICoachGoalTypeForDecisionType,
  MonetaryGoalDecisionTypes,
  DebtPayoffDecisionTypes,
  resolveGoalFromAction,
} from 'components/FinancialCheckup/FinancialDashboard/GoalsSection/useGoal';
import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { GetApplicationData } from 'handlers/applicationData';
import { getGoalOptimizer } from 'selectors/goalOptimizer';
import { RoutePath } from 'enums/Routes';
import {
  getInternalMessageOptions,
  PharaphraseType,
  AICoachActions,
  consolidateDebtAction,
  getParaphrasedSuccessMessage,
  getGoalDescription,
} from 'utils/aiCoachUtils';
import { getBankAccount } from 'selectors/getBankAccount';
import { CurrentFlow } from 'enums/CurrentFlow';
import { hasLoanOfferToShow, isProbableLoanOffer } from 'components/FinancialCheckup/loanOfferAccess';
import usePursueLoanOffer from 'hooks/usePursueLoanOffer';

interface UseGoalActionHandlerParams {
  application: GetApplicationData;
  aiCoachGoals: AICoachGoalItem[] | undefined;
  decisionActions: Action[];
}

interface HandleDecisionActionResult {
  handled: boolean;
  needsPlaid: boolean;
}

export const useGoalActionHandler = ({ application, aiCoachGoals, decisionActions }: UseGoalActionHandlerParams) => {
  const dispatch = useDispatch();
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const navigate = useNavigate();
  const { messages, recommendedPrimaryGoal } = useSelector(getGoalOptimizer);
  const applicationId = application?.id;
  const { savingsAccounts } = useSelector(getBankAccount);
  const isOfferAvailable = hasLoanOfferToShow(application!.status);
  const isProbableOffer = isProbableLoanOffer(application!.status);
  const { pursueLoan } = usePursueLoanOffer();

  const handleDecisionAction = useCallback(
    (action: Action): HandleDecisionActionResult => {
      if (!applicationId || !action.goals || action.goals.length === 0) {
        return { handled: false, needsPlaid: false };
      }

      // Use centralized logic to resolve goal information
      const resolvedGoal = resolveGoalFromAction(action.goals, aiCoachGoals, isOfferAvailable);

      if (!resolvedGoal) {
        return { handled: false, needsPlaid: false };
      }

      const { decisionType, goalToUpdate, shouldPreserveGoalType } = resolvedGoal;

      let needsPlaidConnection = MonetaryGoalDecisionTypes.includes(decisionType);
      const goalType = getAICoachGoalTypeForDecisionType(decisionType);
      const existingGoal = aiCoachGoals?.find((goal) => goal.type === goalType);

      // Bypass Plaid for student loan debt since we don't need to connect a savings account for this goal
      // Note: OTHER goal type still needs Plaid connection even though it preserves its type
      if (needsPlaidConnection && goalToUpdate?.type === AICoachGoalType.PayDownStudentLoanDebt) {
        needsPlaidConnection = false;
      }

      if (needsPlaidConnection) {
        // Case 1: Needs Plaid connection
        if (!existingGoal?.hasSavingsAccountId) {
          // Fetch savings accounts to check if user has any
          (async () => {
            try {
              let currentAccounts = savingsAccounts ?? [];

              if (currentAccounts.length === 0) {
                const savingsAccountsResponse = await dispatchWithUnwrap(getSavingsAccounts(applicationId));
                currentAccounts = savingsAccountsResponse?.savingsAccounts ?? [];
              }

              if (currentAccounts.length > 0) {
                // If user has one or more savings accounts, open Plaid in fullScreen to show selection
                dispatch(
                  setActiveWorkflow({
                    success: false,
                    type: WorkflowTypes.Plaid,
                    isLoading: true,
                    fullScreen: true,
                  }),
                );

                return { handled: true, needsPlaid: true };
              }

              // No savings accounts found
              dispatch(updateLastMessage({ userResponse: action.actionText }));
              const internalMessage = getInternalMessageOptions(decisionType, PharaphraseType.ConnectSavingsAccount);

              // Call paraphrase API to get the paraphrased text and add it as a new message
              if (internalMessage?.text) {
                dispatchWithUnwrap(paraphraseGoal({ applicationId, text: internalMessage.text }))
                  .then((response) => {
                    dispatch(
                      addMessage({
                        explanation: response.paraphrasedText,
                        actions: internalMessage.actions,
                      }),
                    );
                  })
                  .catch((error: unknown) => {
                    analytics.track('AI Coach - Failed to paraphrase message', { error });
                  });

                return { handled: true, needsPlaid: true };
              }
            } catch (error: unknown) {
              analytics.track('AI Coach - Failed to fetch savings accounts', { error });
              // Fallback to Plaid if fetch fails
              dispatch(
                setActiveWorkflow({
                  success: false,
                  type: WorkflowTypes.Plaid,
                  isLoading: true,
                  sourceActionId: action.id,
                  openPlaid: true,
                }),
              );
            }
          })().catch((error: unknown) => {
            analytics.track('AI Coach - Failed to fetch savings accounts', { error });
          });

          return { handled: true, needsPlaid: true };
        }
      } else {
        // Case 2: Does NOT need Plaid - update goal directly

        // If shouldPreserveGoalType is true (e.g., student loan or OTHER with SaveForSpecificExpense),
        // we update the existing goal with the decision type instead of creating a new goal
        const upsertGoal =
          shouldPreserveGoalType && goalToUpdate
            ? {
                ...goalToUpdate,
                type: goalToUpdate.type,
                decisionType,
                priority: AICoachGoalPriority.Primary,
              }
            : {
                ...(existingGoal ?? { type: goalType }),
                type: goalType,
                decisionType,
                priority: AICoachGoalPriority.Primary,
              };

        const existingGoals = aiCoachGoals ?? [];
        let nextGoals: AICoachGoalItem[];

        if (shouldPreserveGoalType && goalToUpdate) {
          // Update the existing goal (student loan or OTHER) with the decision type
          nextGoals = existingGoals.map((g) => (g.type === goalToUpdate.type ? { ...g, ...upsertGoal } : g));
        } else {
          nextGoals = existingGoal
            ? existingGoals.map((g) => (g.type === goalType ? { ...g, ...upsertGoal } : g))
            : [...existingGoals, upsertGoal];
        }

        // TODO: move this to a helper function
        const lastMessageActions = messages[messages.length - 1]?.actions ?? [];
        const nonDecisionActions = lastMessageActions.filter(
          (a: Action) =>
            !a.isDecision && !decisionActions.some((d: Action) => d.id === a.id || d.actionText === a.actionText),
        );

        // Fire and forget
        (async () => {
          await dispatchWithUnwrap(updateAICoachGoals({ applicationId, goals: nextGoals }));
          analytics.track('Goal Confirmed', { goal: decisionType });
          dispatch(getCreditScoreHistory(applicationId));
          dispatch(getGoalProgress(application.borrowerId));
          dispatch(removeDecisionAction(action.actionText));
          dispatch(getNextSteps(application.borrowerId));
          dispatch(getFinancialMetrics(applicationId));

          // Update last message with success indicator
          const successMessage = getGoalDescription(decisionType);
          dispatch(updateLastMessage({ successMessage }));

          const successExplanation = await getParaphrasedSuccessMessage({
            applicationId,
            recommendedPrimaryGoal: recommendedPrimaryGoal ?? decisionType,
            dispatchWithUnwrap,
            paraphraseNextStep,
          });

          const shouldShowConsolidateButton = DebtPayoffDecisionTypes.includes(decisionType) && isOfferAvailable;
          const finalAction = shouldShowConsolidateButton
            ? consolidateDebtAction(isProbableOffer)
            : AICoachActions.CheckMyDashboard;

          dispatch(addMessage({ explanation: successExplanation, actions: [...nonDecisionActions, finalAction] }));
        })().catch((error: unknown) => {
          analytics.track('AI Coach - Failed to update goal', { error });
        });

        return { handled: true, needsPlaid: false };
      }

      return { handled: false, needsPlaid: false };
    },
    [
      applicationId,
      aiCoachGoals,
      decisionActions,
      messages,
      dispatch,
      dispatchWithUnwrap,
      navigate,
      isOfferAvailable,
      isProbableOffer,
    ],
  );

  const handleActionOverride = useCallback(
    async (action: Action): Promise<boolean> => {
      if (!action.actionOverride) {
        return false;
      }

      switch (action.actionOverride) {
        case ActionOverrideType.ToggleAIChat:
          dispatch(toggleAIChat());
          return true;
        case ActionOverrideType.NavigateToOfferAvailable:
          dispatch(toggleAIChat());
          // Switch flow to Debt Consolidation before navigating
          try {
            await dispatchWithUnwrap(
              updateApplicationCurrentFlow({
                applicationId: applicationId!,
                currentFlow: CurrentFlow.DebtConsolidation,
              }),
            );
          } catch (error) {
            analytics.track('Failed to switch to Debt Consolidation flow', { error });
          }
          navigate(RoutePath.OfferAvailable);
          return true;
        case ActionOverrideType.PursueProbableLoan:
          dispatch(toggleAIChat());
          await pursueLoan();
          return true;
        default:
          return false;
      }
    },
    [dispatch, navigate, applicationId, dispatchWithUnwrap, pursueLoan],
  );

  return { handleDecisionAction, handleActionOverride };
};
